import { Link } from "react-router-dom";
import Layout, { Section } from "@/components/layout/Layout";
import useNoIndex from "@/lib/useNoIndex";
import useOgMeta from "@/lib/useOgMeta";
import { ArrowLeft } from "lucide-react";

const TITLE = "Page not found — Ventara";
const DESCRIPTION = "The page you're looking for doesn't exist, may have moved, or the link is out of date.";

// Catch-all route for any path that doesn't match a real page. The SPA
// server answers with HTTP 200 (see docs/decisions/006-seo-analytics-and-
// indexing.md for why a real HTTP 404 needs static rendering/SSR and is
// deferred to Phase 2), so this page carries its own noindex directive and
// explicit Open Graph values rather than a false 200 status. `notFound` on
// Layout also suppresses canonical/hreflang alternates/og:url for this
// route — an invalid URL should never declare itself canonical. useOgMeta
// restores whatever og:title/description/type/image were set before this
// page mounted (or removes them if they didn't exist) on unmount, so none
// of the 404's values leak into whatever page the visitor lands on next.
export default function NotFound() {
    useNoIndex();
    useOgMeta({ title: TITLE, description: DESCRIPTION, type: "website", image: null, twitterImage: null });

    return (
        <Layout title={TITLE} description={DESCRIPTION} notFound>
            <Section className="pt-40 pb-32" data-testid="not-found">
                <p className="label">404</p>
                <h1 className="font-serif font-light text-6xl sm:text-7xl mt-6 tracking-tight">
                    This page has wandered off.
                </h1>
                <p className="prose-body text-lg mt-8 max-w-xl">{DESCRIPTION}</p>
                <Link to="/" className="mt-10 inline-flex items-center gap-2 text-sm border-b border-foreground/40 pb-1">
                    <ArrowLeft className="h-4 w-4" /> Back to Home
                </Link>
            </Section>
        </Layout>
    );
}
